import React, { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, Quote, Eye, Code, Pencil,
} from "lucide-react";

/**
 * Lightweight markdown editor with toolbar.
 * - Stores plain markdown in `value`.
 * - Toolbar buttons wrap/insert markdown around the textarea selection.
 * - "Preview" toggle renders the markdown via react-markdown.
 *
 * Backwards-compatible: existing plain-text bodies render fine
 * (paragraphs auto-detected, line breaks preserved).
 */
export function MarkdownEditor({ value, onChange, placeholder, testId, minRows = 8 }) {
  const ref = useRef(null);
  const [mode, setMode] = useState("write"); // write | preview

  const apply = (before, after = "", placeholder_ = "") => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = (value || "").slice(start, end) || placeholder_;
    const next = (value || "").slice(0, start) + before + sel + after + (value || "").slice(end);
    onChange(next);
    // restore caret around the inserted token
    requestAnimationFrame(() => {
      ta.focus();
      const caretStart = start + before.length;
      ta.setSelectionRange(caretStart, caretStart + sel.length);
    });
  };

  const applyLinePrefix = (prefix) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    let end = ta.selectionEnd;
    const text = value || "";
    // If the caret has no selection, extend the range to the full current line
    // so the toolbar button still prefixes the line the cursor is on.
    if (start === end) {
      const eol = text.indexOf("\n", start);
      end = eol === -1 ? text.length : eol;
    }
    // find start of the first selected line
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    // selected block
    const block = text.slice(lineStart, end);
    // toggle: if every non-empty line already starts with prefix, strip it; else add it
    const lines = block.split("\n");
    const nonEmptyLines = lines.filter((l) => l.trim());
    const allHavePrefix = nonEmptyLines.length > 0 && nonEmptyLines.every((l) => l.startsWith(prefix));
    const newBlock = lines.map((l) => {
      if (!l.trim()) return l;
      return allHavePrefix ? l.slice(prefix.length) : prefix + l;
    }).join("\n");
    // If the block was completely empty (e.g. caret on a blank line), insert the prefix as a stub
    const finalBlock = newBlock || prefix;
    const next = text.slice(0, lineStart) + finalBlock + text.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(lineStart + finalBlock.length, lineStart + finalBlock.length);
    });
  };

  const insertLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    apply("[", `](${url})`, "link text");
  };

  const tools = [
    { icon: Bold, label: "Bold (Ctrl+B)", fn: () => apply("**", "**", "bold text"), tid: "md-bold" },
    { icon: Italic, label: "Italic (Ctrl+I)", fn: () => apply("*", "*", "italic"), tid: "md-italic" },
    { icon: Heading2, label: "Heading 2", fn: () => applyLinePrefix("## "), tid: "md-h2" },
    { icon: Heading3, label: "Heading 3", fn: () => applyLinePrefix("### "), tid: "md-h3" },
    { icon: List, label: "Bulleted list", fn: () => applyLinePrefix("- "), tid: "md-ul" },
    { icon: ListOrdered, label: "Numbered list", fn: () => applyLinePrefix("1. "), tid: "md-ol" },
    { icon: Quote, label: "Quote", fn: () => applyLinePrefix("> "), tid: "md-quote" },
    { icon: Code, label: "Inline code", fn: () => apply("`", "`", "code"), tid: "md-code" },
    { icon: LinkIcon, label: "Link", fn: insertLink, tid: "md-link" },
  ];

  const onKeyDown = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const k = e.key.toLowerCase();
    if (k === "b") { e.preventDefault(); apply("**", "**", "bold text"); }
    else if (k === "i") { e.preventDefault(); apply("*", "*", "italic"); }
    else if (k === "k") { e.preventDefault(); insertLink(); }
  };

  return (
    <div className="rounded-md border hairline overflow-hidden surface-2" data-testid={testId}>
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b hairline bg-[#0a0e1a]">
        {mode === "write" && tools.map((t) => (
          <button key={t.tid} type="button"
            onClick={t.fn}
            title={t.label}
            data-testid={t.tid}
            className="p-1.5 rounded hover:bg-white/[0.06] text-slate-400 hover:text-slate-200 transition-colors">
            <t.icon className="w-3.5 h-3.5" />
          </button>
        ))}
        <div className="flex-1" />
        <div className="flex items-center gap-0.5 rounded border hairline overflow-hidden">
          <button type="button"
            onClick={() => setMode("write")}
            data-testid="md-mode-write"
            className={`px-2 py-1 text-[11px] uppercase tracking-wider num inline-flex items-center gap-1 ${
              mode === "write" ? "bg-blue-600/20 text-blue-200" : "text-slate-400 hover:text-slate-200"
            }`}>
            <Pencil className="w-3 h-3" /> Write
          </button>
          <button type="button"
            onClick={() => setMode("preview")}
            data-testid="md-mode-preview"
            className={`px-2 py-1 text-[11px] uppercase tracking-wider num inline-flex items-center gap-1 ${
              mode === "preview" ? "bg-blue-600/20 text-blue-200" : "text-slate-400 hover:text-slate-200"
            }`}>
            <Eye className="w-3 h-3" /> Preview
          </button>
        </div>
      </div>

      {mode === "write" ? (
        <Textarea
          ref={ref}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || "Write in Markdown… **bold**, *italic*, ## heading, - list"}
          className="border-0 rounded-none bg-[#0a0e1a] focus-visible:ring-0 font-mono text-[13px] leading-relaxed resize-y"
          style={{ minHeight: `${minRows * 24}px` }}
          data-testid={`${testId}-textarea`}
        />
      ) : (
        <div className="prose prose-invert max-w-none p-4 bg-[#0a0e1a] text-sm leading-relaxed"
          style={{ minHeight: `${minRows * 24}px` }}
          data-testid={`${testId}-preview`}>
          {(value || "").trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          ) : (
            <div className="text-slate-500 italic text-sm">Nothing to preview yet.</div>
          )}
        </div>
      )}

      <div className="px-2 py-1 text-[10px] text-slate-500 border-t hairline bg-[#0a0e1a]">
        Markdown supported · <span className="num">Ctrl+B</span> bold, <span className="num">Ctrl+I</span> italic, <span className="num">Ctrl+K</span> link
      </div>
    </div>
  );
}

/** Simple readonly markdown renderer used in detail view. */
export function MarkdownView({ source, className = "" }) {
  return (
    <div className={`prose prose-invert max-w-none text-sm leading-relaxed ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{source || ""}</ReactMarkdown>
    </div>
  );
}
